import { useState, useEffect } from 'react';
import { AdminLayout } from './AdminDashboard';
import { Search, Filter, Eye, Check, X, Trash2, ChevronDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { API_URL, formatDate, centerNames } from '../../lib/utils';
import { authFetch } from '../../lib/auth';
import { toast } from 'sonner';

const statusColors = {
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'En attente' },
  reviewed: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Examiné' },
  accepted: { bg: 'bg-green-100', text: 'text-green-700', label: 'Accepté' },
  rejected: { bg: 'bg-red-100', text: 'text-red-700', label: 'Refusé' }
};

export default function AdminPreregistrations() {
  const [preregistrations, setPreregistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCenter, setFilterCenter] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedPrereg, setSelectedPrereg] = useState(null);

  useEffect(() => {
    fetchPreregistrations();
  }, [filterCenter, filterStatus]);

  const fetchPreregistrations = async () => {
    try {
      let url = `${API_URL}/preregistrations`;
      const params = new URLSearchParams();
      if (filterCenter) params.append('center', filterCenter);
      if (filterStatus) params.append('status', filterStatus);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await authFetch(url);
      if (response.ok) {
        const data = await response.json();
        setPreregistrations(data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const response = await authFetch(`${API_URL}/preregistrations/${id}?status=${status}`, {
        method: 'PATCH',
      });
      if (response.ok) {
        toast.success('Statut mis à jour');
        fetchPreregistrations();
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deletePrereg = async (id) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette préinscription ?')) return;
    
    try {
      const response = await authFetch(`${API_URL}/preregistrations/${id}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        toast.success('Préinscription supprimée');
        fetchPreregistrations();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredData = preregistrations.filter(p => {
    const searchLower = search.toLowerCase();
    return (
      p.first_name?.toLowerCase().includes(searchLower) ||
      p.last_name?.toLowerCase().includes(searchLower) ||
      p.email?.toLowerCase().includes(searchLower) ||
      p.program?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <AdminLayout>
      <div data-testid="admin-preregistrations">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-[#003366]">Préinscriptions</h1>
            <p className="text-gray-500 mt-1">{preregistrations.length} préinscriptions au total</p>
          </div>
        </div>

        {/* Filters */}
        <div className="admin-card mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <Input
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-preregistrations"
              />
            </div>
            <Select value={filterCenter || "all"} onValueChange={(v) => setFilterCenter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Centre" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les centres</SelectItem>
                <SelectItem value="diplomante">Diplômant</SelectItem>
                <SelectItem value="qualifiante">Qualifiant</SelectItem>
                <SelectItem value="certifiante">Certifiant</SelectItem>
                <SelectItem value="linguistique">Linguistique</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="reviewed">Examiné</SelectItem>
                <SelectItem value="accepted">Accepté</SelectItem>
                <SelectItem value="rejected">Refusé</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        <div className="admin-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-3 text-sm font-semibold text-gray-500">Candidat</th>
                  <th className="px-4 py-3 text-sm font-semibold text-gray-500">Contact</th>
                  <th className="px-4 py-3 text-sm font-semibold text-gray-500">Centre</th>
                  <th className="px-4 py-3 text-sm font-semibold text-gray-500">Programme</th>
                  <th className="px-4 py-3 text-sm font-semibold text-gray-500">Statut</th>
                  <th className="px-4 py-3 text-sm font-semibold text-gray-500">Date</th>
                  <th className="px-4 py-3 text-sm font-semibold text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((prereg) => (
                  <tr key={prereg.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <p className="font-medium text-[#003366]">{prereg.first_name} {prereg.last_name}</p>
                      <p className="text-sm text-gray-500">{prereg.education_level || '-'}</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-gray-600">{prereg.email}</p>
                      <p className="text-sm text-gray-500">{prereg.phone}</p>
                    </td>
                    <td className="px-4 py-4">
                      <span className="badge badge-blue text-xs">{centerNames[prereg.center] || prereg.center}</span>
                    </td>
                    <td className="px-4 py-4 text-gray-600 text-sm max-w-[200px] truncate">
                      {prereg.program}
                    </td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[prereg.status]?.bg} ${statusColors[prereg.status]?.text}`}>
                        {statusColors[prereg.status]?.label || prereg.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-gray-500 text-sm">
                      {formatDate(prereg.created_at)}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setSelectedPrereg(prereg)}
                          className="h-8 w-8 p-0"
                        >
                          <Eye size={16} />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                              <ChevronDown size={16} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => updateStatus(prereg.id, 'reviewed')}>
                              <Eye size={14} className="mr-2" /> Marquer examiné
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => updateStatus(prereg.id, 'accepted')}>
                              <Check size={14} className="mr-2 text-green-600" /> Accepter
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => updateStatus(prereg.id, 'rejected')}>
                              <X size={14} className="mr-2 text-red-600" /> Refuser
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => deletePrereg(prereg.id)} className="text-red-600">
                              <Trash2 size={14} className="mr-2" /> Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredData.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      Aucune préinscription trouvée
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail Dialog */}
        <Dialog open={!!selectedPrereg} onOpenChange={() => setSelectedPrereg(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#003366]">
                Détails de la préinscription
              </DialogTitle>
            </DialogHeader>
            {selectedPrereg && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Prénom</p>
                    <p className="font-medium">{selectedPrereg.first_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Nom</p>
                    <p className="font-medium">{selectedPrereg.last_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{selectedPrereg.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Téléphone</p>
                    <p className="font-medium">{selectedPrereg.phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Centre</p>
                    <p className="font-medium">{centerNames[selectedPrereg.center] || selectedPrereg.center}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Programme</p>
                    <p className="font-medium">{selectedPrereg.program}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Niveau</p>
                    <p className="font-medium">{selectedPrereg.education_level || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Statut</p>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[selectedPrereg.status]?.bg} ${statusColors[selectedPrereg.status]?.text}`}>
                      {statusColors[selectedPrereg.status]?.label}
                    </span>
                  </div>
                </div>
                {selectedPrereg.motivation && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Motivation</p>
                    <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">{selectedPrereg.motivation}</p>
                  </div>
                )}
                <div className="flex gap-2 pt-4">
                  <Button onClick={() => updateStatus(selectedPrereg.id, 'accepted')} className="flex-1 bg-green-600 hover:bg-green-700">
                    <Check size={16} className="mr-2" /> Accepter
                  </Button>
                  <Button onClick={() => updateStatus(selectedPrereg.id, 'rejected')} variant="destructive" className="flex-1">
                    <X size={16} className="mr-2" /> Refuser
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
